import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { StatusBadge } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { formatDate, formatDateTime } from '../lib/formatters';
import { useAuth } from '../contexts/AuthContext';

type ServiceType =
  'dia' | 'broadband' | 'lte' | 'sip' | 'p2p' | 'colo' | 'dark_fiber' | 'other';
type ServiceStatus =
  'quoting' | 'installing' | 'active' | 'expiring' | 'disconnected';
interface Notes {
  id: string;
  location_id: string;
  source_site_id: string | null;
  address: string | null;
  city_state_zip: string | null;
  phone: string | null;
  property_contact: string | null;
  property_email: string | null;
  access_notes: string | null;
  escalation_notes: string | null;
  parking_notes: string | null;
  equipment_notes: string | null;
  timezone: string | null;
  lat: number | null;
  lng: number | null;
  last_visit_at: string | null;
  last_visit_technician: string | null;
  last_visit_summary: string | null;
  open_order_count: number | null;
  updated_at: string;
}
interface Service {
  id: string;
  location_id: string;
  code: string | null;
  type: ServiceType;
  provider: string | null;
  circuit_id: string | null;
  bandwidth_down_mbps: number | null;
  bandwidth_up_mbps: number | null;
  bandwidth_label: string | null;
  transport: string | null;
  handoff: string | null;
  static_cidr: string | null;
  bgp_enabled: boolean;
  bgp_asn: string | null;
  sla_target: string | null;
  mttr_hours: number | null;
  wan_device_id: string | null;
  status: ServiceStatus;
  is_customer_visible: boolean;
  source_record_id: string | null;
  updated_at: string;
}
interface Commercial {
  id: string;
  updated_at: string;
  service_id: string;
  mrc: number | null;
  nrc: number | null;
  currency: string | null;
  ban: string | null;
  account_number: string | null;
  term_months: number | null;
  start_date: string | null;
  end_date: string | null;
  notice_period_days: number | null;
  auto_renew: boolean | null;
  notes: string | null;
}
interface Visit {
  id: string;
  location_id: string;
  visited_at: string;
  technician_name: string | null;
  work_performed: string | null;
  is_customer_visible: boolean;
  source_record_id: string | null;
  updated_at: string;
}
interface PrivateVisit {
  id: string;
  visit_id: string;
  follow_up: string | null;
  updated_at: string;
}
const inputClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary';
const buttonClass =
  'min-h-11 rounded-md border border-border px-3 py-2 text-sm text-text hover:bg-surface-3 disabled:opacity-50';
const primaryClass = `${buttonClass} border-primary bg-primary text-white hover:bg-primary-hover`;
const serviceTypes: ServiceType[] = [
  'dia',
  'broadband',
  'lte',
  'sip',
  'p2p',
  'colo',
  'dark_fiber',
  'other',
];
const statuses: ServiceStatus[] = [
  'quoting',
  'installing',
  'active',
  'expiring',
  'disconnected',
];
const label = (value: string) => value.replace(/_/g, ' ');
const text = (data: FormData, key: string) =>
  String(data.get(key) ?? '').trim() || null;
const numeric = (data: FormData, key: string) =>
  text(data, key) === null ? null : Number(data.get(key));

export function LocationOperationsPage() {
  const { id } = useParams<{ id: string }>();
  return id ? <LocationOperationsWorkspace key={id} locationId={id} /> : null;
}

export function LocationOperationsWorkspace({
  locationId,
}: {
  locationId: string;
}) {
  const client = useQueryClient();
  const { role } = useAuth();
  const canSeeCommercial =
    role === 'account_manager' ||
    role === 'billing_admin' ||
    role === 'admin' ||
    role === 'superadmin';
  const queryKey = ['location-operations', locationId];
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [location, notes, services, visits, history] = await Promise.all([
        supabase
          .from('locations')
          .select('id, name')
          .eq('id', locationId)
          .is('deleted_at', null)
          .single(),
        supabase
          .from('location_operations_notes')
          .select(
            'id,location_id,source_site_id,address,city_state_zip,phone,property_contact,property_email,access_notes,escalation_notes,parking_notes,equipment_notes,timezone,lat,lng,last_visit_at,last_visit_technician,last_visit_summary,open_order_count,updated_at',
          )
          .eq('location_id', locationId)
          .is('deleted_at', null)
          .order('created_at'),
        supabase
          .from('location_services')
          .select(
            'id,location_id,code,type,provider,circuit_id,bandwidth_down_mbps,bandwidth_up_mbps,bandwidth_label,transport,handoff,static_cidr,bgp_enabled,bgp_asn,sla_target,mttr_hours,wan_device_id,status,is_customer_visible,source_record_id,updated_at',
          )
          .eq('location_id', locationId)
          .is('deleted_at', null)
          .order('code'),
        supabase
          .from('location_visit_history')
          .select(
            'id,location_id,visited_at,technician_name,work_performed,is_customer_visible,source_record_id,updated_at',
          )
          .eq('location_id', locationId)
          .is('deleted_at', null)
          .order('visited_at', { ascending: false }),
        supabase
          .from('location_operations_activity')
          .select('id,event_type,title,detail,occurred_at')
          .eq('location_id', locationId)
          .order('occurred_at', { ascending: false })
          .limit(100),
      ]);
      for (const result of [location, notes, services, visits, history])
        if (result.error) throw result.error;
      const serviceRows = (services.data ?? []) as unknown as Service[];
      const visitRows = (visits.data ?? []) as unknown as Visit[];
      const [commercial, followUps] = await Promise.all([
        canSeeCommercial && serviceRows.length
          ? supabase
              .from('location_service_private')
              .select(
                'id,updated_at,service_id,mrc,nrc,currency,ban,account_number,term_months,start_date,end_date,notice_period_days,auto_renew,notes',
              )
              .in(
                'service_id',
                serviceRows.map((row) => row.id),
              )
          : Promise.resolve({ data: [], error: null }),
        visitRows.length
          ? supabase
              .from('location_visit_private')
              .select('id,updated_at,visit_id,follow_up')
              .in(
                'visit_id',
                visitRows.map((row) => row.id),
              )
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (commercial.error) throw commercial.error;
      if (followUps.error) throw followUps.error;
      return {
        name: location.data!.name,
        notes: (notes.data ?? []) as Notes[],
        services: serviceRows,
        visits: visitRows,
        history: history.data ?? [],
        commercial: (commercial.data ?? []).filter((row) =>
          serviceRows.some((service) => service.id === row.service_id),
        ) as unknown as Commercial[],
        followUps: new Map(
          (followUps.data ?? [])
            .filter((row) =>
              visitRows.some((visit) => visit.id === row.visit_id),
            )
            .map((row) => [row.visit_id, row as PrivateVisit]),
        ),
      };
    },
  });
  const [addingNotes, setAddingNotes] = useState(false);
  const [serviceEditor, setServiceEditor] = useState<Service | 'new' | null>(
    null,
  );
  const [visitEditor, setVisitEditor] = useState<Visit | 'new' | null>(null);
  const [commercialId, setCommercialId] = useState<string | null>(null);
  const [followUpId, setFollowUpId] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<{
    kind: 'service' | 'visit';
    id: string;
    updatedAt: string;
  } | null>(null);
  const invalidate = () => client.invalidateQueries({ queryKey });
  const archive = useMutation({
    mutationFn: async (target: {
      kind: 'service' | 'visit';
      id: string;
      updatedAt: string;
    }) => {
      const result = await supabase
        .from(
          target.kind === 'service'
            ? 'location_services'
            : 'location_visit_history',
        )
        .update({
          deleted_at: new Date().toISOString(),
          is_customer_visible: false,
        })
        .eq('id', target.id)
        .eq('location_id', locationId)
        .eq('updated_at', target.updatedAt)
        .select('id')
        .single();
      checkWrite(result);
    },
    onSuccess: async () => {
      setArchiveTarget(null);
      await invalidate();
    },
  });
  if (query.isLoading)
    return (
      <p role="status" className="text-sm text-text-muted">
        Loading site operations…
      </p>
    );
  if (query.isError)
    return (
      <ErrorMessage error={query.error} retry={() => void query.refetch()} />
    );
  if (!query.data) return null;
  const data = query.data;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text">Site operations</h1>
        <p className="mt-1 text-sm text-text-muted">
          Documentation, connectivity services and visit history for {data.name}
          .
        </p>
      </div>
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold text-text">
            Internal site notes ({data.notes.length})
          </h2>
          <button
            type="button"
            className={buttonClass}
            onClick={() => setAddingNotes(true)}
          >
            Add site notes
          </button>
        </div>
        {data.notes.map((note) => (
          <NotesEditor
            key={note.id}
            locationId={locationId}
            notes={note}
            onSaved={invalidate}
          />
        ))}
        {(addingNotes || data.notes.length === 0) && (
          <NotesEditor
            key="new"
            locationId={locationId}
            notes={null}
            onSaved={async () => {
              setAddingNotes(false);
              await invalidate();
            }}
          />
        )}
      </section>
      <section
        className="space-y-4 rounded-lg border border-border bg-surface p-4 sm:p-5"
        aria-labelledby="services-title"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id="services-title" className="font-semibold text-text">
              Circuits and services ({data.services.length})
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              Customer access is off until a record is reviewed and published.
              Commercial details always stay internal.
            </p>
          </div>
          <button
            type="button"
            className={buttonClass}
            onClick={() => setServiceEditor('new')}
          >
            Add service
          </button>
        </div>
        {serviceEditor && (
          <ServiceEditor
            key={serviceEditor === 'new' ? 'new' : serviceEditor.id}
            locationId={locationId}
            service={serviceEditor === 'new' ? null : serviceEditor}
            onCancel={() => setServiceEditor(null)}
            onSaved={async () => {
              setServiceEditor(null);
              await invalidate();
            }}
          />
        )}
        {!data.services.length && (
          <p className="py-3 text-sm text-text-muted">
            No services recorded for this location.
          </p>
        )}
        <div className="space-y-3">
          {data.services.map((service) => {
            const commercial = data.commercial.find(
              (row) => row.service_id === service.id,
            );
            return (
              <article
                key={service.id}
                className="rounded-lg border border-border bg-surface-2 p-4"
              >
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <h3 className="font-medium text-text">
                      {service.code ??
                        service.circuit_id ??
                        service.provider ??
                        'Service'}
                    </h3>
                    <p className="mt-1 text-xs text-text-muted">
                      {[
                        service.provider,
                        label(service.type),
                        service.circuit_id,
                      ]
                        .filter(Boolean)
                        .join(' · ')}{' '}
                      ·{' '}
                      {service.is_customer_visible
                        ? 'Published to customer'
                        : 'Internal'}
                      {service.source_record_id ? ' · Migrated record' : ''}
                    </p>
                  </div>
                  <StatusBadge status={service.status} />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm lg:grid-cols-4">
                  <Fact
                    title="Bandwidth"
                    value={
                      service.bandwidth_label ??
                      [
                        service.bandwidth_down_mbps == null
                          ? null
                          : `${service.bandwidth_down_mbps} down`,
                        service.bandwidth_up_mbps == null
                          ? null
                          : `${service.bandwidth_up_mbps} up`,
                      ]
                        .filter(Boolean)
                        .join(' / ') +
                        (service.bandwidth_down_mbps != null ||
                        service.bandwidth_up_mbps != null
                          ? ' Mbps'
                          : '')
                    }
                  />
                  <Fact
                    title="Transport / handoff"
                    value={[service.transport, service.handoff]
                      .filter(Boolean)
                      .join(' / ')}
                  />
                  <Fact
                    title="SLA / restoration"
                    value={[
                      service.sla_target,
                      service.mttr_hours == null
                        ? null
                        : `${service.mttr_hours} h`,
                    ]
                      .filter(Boolean)
                      .join(' / ')}
                  />
                  <Fact
                    title="Term ends"
                    value={
                      canSeeCommercial && commercial?.end_date
                        ? formatDate(commercial.end_date)
                        : null
                    }
                  />
                </dl>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={buttonClass}
                    onClick={() => setServiceEditor(service)}
                  >
                    Edit service
                  </button>
                  {canSeeCommercial && (
                    <button
                      type="button"
                      className={buttonClass}
                      onClick={() =>
                        setCommercialId(
                          commercialId === service.id ? null : service.id,
                        )
                      }
                    >
                      {commercialId === service.id
                        ? 'Close commercial details'
                        : 'Commercial details'}
                    </button>
                  )}
                  <button
                    type="button"
                    className={buttonClass}
                    onClick={() =>
                      setArchiveTarget({
                        kind: 'service',
                        id: service.id,
                        updatedAt: service.updated_at,
                      })
                    }
                  >
                    Archive service
                  </button>
                </div>
                {canSeeCommercial && commercialId === service.id && (
                  <CommercialEditor
                    serviceId={service.id}
                    commercial={commercial}
                    onSaved={invalidate}
                  />
                )}
              </article>
            );
          })}
        </div>
      </section>
      <section
        className="space-y-4 rounded-lg border border-border bg-surface p-4 sm:p-5"
        aria-labelledby="visits-title"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id="visits-title" className="font-semibold text-text">
              Visit history ({data.visits.length})
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              Historical work completed at the site. Schedule new work through
              Field Trips.
            </p>
          </div>
          <button
            type="button"
            className={buttonClass}
            onClick={() => setVisitEditor('new')}
          >
            Record visit
          </button>
        </div>
        {visitEditor && (
          <VisitEditor
            key={visitEditor === 'new' ? 'new' : visitEditor.id}
            locationId={locationId}
            visit={visitEditor === 'new' ? null : visitEditor}
            onCancel={() => setVisitEditor(null)}
            onSaved={async () => {
              setVisitEditor(null);
              await invalidate();
            }}
          />
        )}
        {!data.visits.length && (
          <p className="py-3 text-sm text-text-muted">
            No historical visits recorded for this location.
          </p>
        )}
        <ul className="divide-y divide-divider">
          {data.visits.map((visit) => (
            <li key={visit.id} className="space-y-3 py-4">
              <div className="flex flex-wrap justify-between gap-2">
                <h3 className="text-sm font-medium text-text">
                  {formatDateTime(visit.visited_at)}
                  {visit.technician_name ? ` · ${visit.technician_name}` : ''}
                </h3>
                <span className="text-xs text-text-muted">
                  {visit.is_customer_visible
                    ? 'Published to customer'
                    : 'Internal'}
                  {visit.source_record_id ? ' · Migrated record' : ''}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-text-muted">
                {visit.work_performed ?? 'No work summary recorded.'}
              </p>
              {data.followUps.get(visit.id)?.follow_up && (
                <p className="whitespace-pre-wrap rounded-md border border-border bg-surface-2 p-3 text-sm text-text">
                  <strong>Internal follow-up: </strong>
                  {data.followUps.get(visit.id)?.follow_up}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={buttonClass}
                  onClick={() => setVisitEditor(visit)}
                >
                  Edit visit
                </button>
                <button
                  type="button"
                  className={buttonClass}
                  onClick={() =>
                    setFollowUpId(followUpId === visit.id ? null : visit.id)
                  }
                >
                  Internal follow-up
                </button>
                <button
                  type="button"
                  className={buttonClass}
                  onClick={() =>
                    setArchiveTarget({
                      kind: 'visit',
                      id: visit.id,
                      updatedAt: visit.updated_at,
                    })
                  }
                >
                  Archive visit
                </button>
              </div>
              {followUpId === visit.id && (
                <FollowUpEditor
                  visitId={visit.id}
                  record={data.followUps.get(visit.id)}
                  onSaved={invalidate}
                />
              )}
            </li>
          ))}
        </ul>
      </section>
      <section
        className="rounded-lg border border-border bg-surface p-4 sm:p-5"
        aria-labelledby="imported-activity-title"
      >
        <h2 id="imported-activity-title" className="font-semibold text-text">
          Imported site activity
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          Historical changes from Mission Control are retained separately from
          the current site records. Showing the latest 100 events.
        </p>
        {!data.history.length ? (
          <p className="mt-3 text-sm text-text-muted">
            No imported events for this site.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-divider">
            {data.history.map((event) => (
              <li key={event.id} className="py-3">
                <p className="text-sm font-medium text-text">{event.title}</p>
                <p className="mt-1 text-xs text-text-muted">
                  {formatDateTime(event.occurred_at)} ·{' '}
                  {event.event_type.replace(/_/g, ' ')}
                </p>
                {event.detail && (
                  <p className="mt-1 whitespace-pre-wrap text-sm text-text-muted">
                    {event.detail}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      {archiveTarget && (
        <section
          role="alertdialog"
          aria-labelledby="archive-title"
          aria-describedby="archive-description"
          className="rounded-lg border border-warning bg-surface p-5"
        >
          <h2 id="archive-title" className="font-semibold text-text">
            Archive this {archiveTarget.kind}?
          </h2>
          <p id="archive-description" className="mt-2 text-sm text-text-muted">
            It will be removed from the active site record and customer view.
            The historical record is retained.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className={primaryClass}
              disabled={archive.isPending}
              onClick={() => archive.mutate(archiveTarget)}
            >
              Confirm archive
            </button>
            <button
              type="button"
              className={buttonClass}
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </button>
          </div>
          {archive.error && <ErrorMessage error={archive.error} />}
        </section>
      )}
    </div>
  );
}

function NotesEditor({
  locationId,
  notes,
  onSaved,
}: {
  locationId: string;
  notes: Notes | null;
  onSaved: () => Promise<unknown>;
}) {
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState(notes?.updated_at);
  const save = useMutation({
    mutationFn: async (form: FormData) => {
      const values = {
        location_id: locationId,
        address: text(form, 'address'),
        city_state_zip: text(form, 'city_state_zip'),
        phone: text(form, 'phone'),
        property_contact: text(form, 'property_contact'),
        property_email: text(form, 'property_email'),
        timezone: text(form, 'timezone'),
        lat: numeric(form, 'lat'),
        lng: numeric(form, 'lng'),
        access_notes: text(form, 'access_notes'),
        parking_notes: text(form, 'parking_notes'),
        escalation_notes: text(form, 'escalation_notes'),
        equipment_notes: text(form, 'equipment_notes'),
      };
      const result = notes
        ? await supabase
            .from('location_operations_notes')
            .update(values)
            .eq('id', notes.id)
            .eq('location_id', locationId)
            .eq('updated_at', expectedUpdatedAt!)
            .select('id,updated_at')
            .single()
        : await supabase
            .from('location_operations_notes')
            .insert(values)
            .select('id,updated_at')
            .single();
      checkWrite(result);
      setExpectedUpdatedAt(result.data!.updated_at);
    },
    onSuccess: onSaved,
  });
  return (
    <section
      className="rounded-lg border border-border bg-surface p-4 sm:p-5"
      aria-label={
        notes?.source_site_id
          ? `Site notes · ${notes.source_site_id}`
          : 'Site notes'
      }
    >
      <h3 className="font-semibold text-text">
        {notes?.source_site_id
          ? `Network ${notes.source_site_id}`
          : 'Site notes'}
      </h3>
      <p className="mt-1 text-sm text-text-muted">
        Access instructions, escalation contacts and equipment notes stay with
        staff. The site profile remains the customer-facing address and contact
        record.
      </p>
      {notes?.source_site_id &&
        (notes.last_visit_at || notes.open_order_count !== null) && (
          <dl className="mt-3 grid gap-3 rounded-md border border-border bg-surface-2 p-3 text-sm sm:grid-cols-2">
            <Fact
              title="Imported last visit"
              value={
                notes.last_visit_at
                  ? `${formatDateTime(notes.last_visit_at)}${notes.last_visit_technician ? ` · ${notes.last_visit_technician}` : ''}`
                  : null
              }
            />
            <Fact
              title="Imported open work orders"
              value={
                notes.open_order_count == null
                  ? null
                  : String(notes.open_order_count)
              }
            />
            {notes.last_visit_summary && (
              <div className="sm:col-span-2">
                <dt className="text-xs text-text-muted">
                  Imported visit summary
                </dt>
                <dd className="mt-1 whitespace-pre-wrap text-text">
                  {notes.last_visit_summary}
                </dd>
              </div>
            )}
          </dl>
        )}
      <form
        className="mt-4 space-y-4"
        onSubmit={(event) => submitForm(event, save.mutate)}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            name="address"
            title="Operations address"
            value={notes?.address}
          />
          <Input
            name="city_state_zip"
            title="City, state and postal code"
            value={notes?.city_state_zip}
          />
          <Input name="timezone" title="Time zone" value={notes?.timezone} />
          <Input
            name="lat"
            title="Latitude"
            type="number"
            min={-90}
            max={90}
            value={notes?.lat}
          />
          <Input
            name="lng"
            title="Longitude"
            type="number"
            min={-180}
            max={180}
            value={notes?.lng}
          />
          <Input
            name="property_contact"
            title="Operations contact"
            value={notes?.property_contact}
          />
          <Input
            name="property_email"
            title="Contact email"
            type="email"
            value={notes?.property_email}
          />
          <Input name="phone" title="Contact phone" value={notes?.phone} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextArea
            name="access_notes"
            title="Access instructions"
            value={notes?.access_notes}
          />
          <TextArea
            name="parking_notes"
            title="Parking and arrival"
            value={notes?.parking_notes}
          />
          <TextArea
            name="escalation_notes"
            title="Escalation instructions"
            value={notes?.escalation_notes}
          />
          <TextArea
            name="equipment_notes"
            title="Equipment notes"
            value={notes?.equipment_notes}
          />
        </div>
        <SaveState mutation={save} label="Save site notes" />
      </form>
    </section>
  );
}

function ServiceEditor({
  locationId,
  service,
  onSaved,
  onCancel,
}: {
  locationId: string;
  service: Service | null;
  onSaved: () => Promise<unknown>;
  onCancel: () => void;
}) {
  const save = useMutation({
    mutationFn: async (form: FormData) => {
      const values = {
        location_id: locationId,
        code: text(form, 'code'),
        type: text(form, 'type') as ServiceType,
        provider: text(form, 'provider'),
        circuit_id: text(form, 'circuit_id'),
        bandwidth_down_mbps: numeric(form, 'bandwidth_down_mbps'),
        bandwidth_up_mbps: numeric(form, 'bandwidth_up_mbps'),
        bandwidth_label: text(form, 'bandwidth_label'),
        transport: text(form, 'transport'),
        handoff: text(form, 'handoff'),
        static_cidr: text(form, 'static_cidr'),
        bgp_enabled: form.has('bgp_enabled'),
        bgp_asn: text(form, 'bgp_asn'),
        sla_target: text(form, 'sla_target'),
        mttr_hours: numeric(form, 'mttr_hours'),
        wan_device_id: text(form, 'wan_device_id'),
        status: text(form, 'status') as ServiceStatus,
        is_customer_visible: form.has('is_customer_visible'),
      };
      const result = service
        ? await supabase
            .from('location_services')
            .update(values)
            .eq('id', service.id)
            .eq('location_id', locationId)
            .eq('updated_at', service.updated_at)
            .select('id')
            .single()
        : await supabase
            .from('location_services')
            .insert(values)
            .select('id')
            .single();
      checkWrite(result);
    },
    onSuccess: onSaved,
  });
  return (
    <form
      aria-label={service ? 'Edit service' : 'Add service'}
      className="space-y-4 rounded-lg border border-primary p-4"
      onSubmit={(event) => submitForm(event, save.mutate)}
    >
      <h3 className="font-medium text-text">
        {service ? 'Edit service' : 'Add service'}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Input name="code" title="Service name / code" value={service?.code} />
        <Select
          name="type"
          title="Service type"
          value={service?.type ?? 'other'}
          options={serviceTypes}
        />
        <Select
          name="status"
          title="Service status"
          value={service?.status ?? 'quoting'}
          options={statuses}
        />
        <Input name="provider" title="Provider" value={service?.provider} />
        <Input
          name="circuit_id"
          title="Circuit ID"
          value={service?.circuit_id}
        />
        <Input
          name="bandwidth_label"
          title="Bandwidth label"
          value={service?.bandwidth_label}
        />
        <Input
          name="bandwidth_down_mbps"
          title="Download (Mbps)"
          type="number"
          value={service?.bandwidth_down_mbps}
        />
        <Input
          name="bandwidth_up_mbps"
          title="Upload (Mbps)"
          type="number"
          value={service?.bandwidth_up_mbps}
        />
        <Input name="transport" title="Transport" value={service?.transport} />
        <Input name="handoff" title="Handoff" value={service?.handoff} />
        <Input
          name="static_cidr"
          title="Static CIDR"
          value={service?.static_cidr}
        />
        <Input name="bgp_asn" title="BGP ASN" value={service?.bgp_asn} />
        <Input
          name="sla_target"
          title="SLA target"
          value={service?.sla_target}
        />
        <Input
          name="mttr_hours"
          title="Restoration target (hours)"
          type="number"
          value={service?.mttr_hours}
        />
        <Input
          name="wan_device_id"
          title="WAN device reference"
          value={service?.wan_device_id}
        />
      </div>
      <Check
        name="bgp_enabled"
        title="BGP enabled"
        checked={service?.bgp_enabled}
      />
      <Check
        name="is_customer_visible"
        title="Publish service details to the customer after review"
        checked={service?.is_customer_visible}
      />
      <p className="text-xs text-text-muted">
        Publishing shares the service, provider, circuit and technical details.
        Commercial costs, account numbers and internal notes stay private.
      </p>
      <SaveState mutation={save} label="Save service" onCancel={onCancel} />
    </form>
  );
}

function CommercialEditor({
  serviceId,
  commercial,
  onSaved,
}: {
  serviceId: string;
  commercial?: Commercial;
  onSaved: () => Promise<unknown>;
}) {
  const [identity, setIdentity] = useState(
    commercial
      ? { id: commercial.id, updated_at: commercial.updated_at }
      : null,
  );
  const save = useMutation({
    mutationFn: async (form: FormData) => {
      const values = {
        service_id: serviceId,
        mrc: numeric(form, 'mrc'),
        nrc: numeric(form, 'nrc'),
        currency: text(form, 'currency')?.toUpperCase() ?? null,
        ban: text(form, 'ban'),
        account_number: text(form, 'account_number'),
        term_months: numeric(form, 'term_months'),
        start_date: text(form, 'start_date'),
        end_date: text(form, 'end_date'),
        notice_period_days: numeric(form, 'notice_period_days'),
        auto_renew:
          text(form, 'auto_renew') === 'unknown'
            ? null
            : text(form, 'auto_renew') === 'yes',
        notes: text(form, 'notes'),
      };
      const result = identity
        ? await supabase
            .from('location_service_private')
            .update(values)
            .eq('id', identity.id)
            .eq('service_id', serviceId)
            .eq('updated_at', identity.updated_at)
            .select('id,updated_at')
            .single()
        : await supabase
            .from('location_service_private')
            .insert(values)
            .select('id,updated_at')
            .single();
      checkWrite(result);
      setIdentity(result.data!);
    },
    onSuccess: onSaved,
  });
  return (
    <form
      aria-label="Commercial details"
      className="mt-4 space-y-4 border-t border-border pt-4"
      onSubmit={(event) => submitForm(event, save.mutate)}
    >
      <h4 className="text-sm font-semibold text-text">
        Commercial details · staff only
      </h4>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Input
          name="mrc"
          title="Monthly recurring cost"
          type="number"
          value={commercial?.mrc}
        />
        <Input
          name="nrc"
          title="One-time cost"
          type="number"
          value={commercial?.nrc}
        />
        <Input
          name="currency"
          title="Currency (ISO code)"
          value={commercial?.currency}
          maxLength={3}
        />
        <Input
          name="ban"
          title="Billing account name / BAN"
          value={commercial?.ban}
        />
        <Input
          name="account_number"
          title="Provider account number"
          value={commercial?.account_number}
        />
        <Input
          name="term_months"
          title="Term (months)"
          type="number"
          value={commercial?.term_months}
        />
        <Input
          name="start_date"
          title="Contract start"
          type="date"
          value={commercial?.start_date}
        />
        <Input
          name="end_date"
          title="Contract end"
          type="date"
          value={commercial?.end_date}
        />
        <Input
          name="notice_period_days"
          title="Cancellation notice (days)"
          type="number"
          value={commercial?.notice_period_days}
        />
        <Select
          name="auto_renew"
          title="Automatic renewal"
          value={
            commercial?.auto_renew == null
              ? 'unknown'
              : commercial.auto_renew
                ? 'yes'
                : 'no'
          }
          options={['unknown', 'yes', 'no']}
        />
      </div>
      <TextArea
        name="notes"
        title="Internal commercial notes"
        value={commercial?.notes}
      />
      <SaveState mutation={save} label="Save commercial details" />
    </form>
  );
}

function VisitEditor({
  locationId,
  visit,
  onSaved,
  onCancel,
}: {
  locationId: string;
  visit: Visit | null;
  onSaved: () => Promise<unknown>;
  onCancel: () => void;
}) {
  const save = useMutation({
    mutationFn: async (form: FormData) => {
      const visitedAt = text(form, 'visited_at');
      if (!visitedAt) throw new Error('Choose a visit date and time.');
      const values = {
        location_id: locationId,
        visited_at: new Date(visitedAt).toISOString(),
        technician_name: text(form, 'technician_name') ?? '',
        work_performed: text(form, 'work_performed') ?? '',
        is_customer_visible: form.has('is_customer_visible'),
      };
      if (!values.technician_name || !values.work_performed)
        throw new Error(
          'Enter the technician and work completed before saving this visit.',
        );
      const result = visit
        ? await supabase
            .from('location_visit_history')
            .update(values)
            .eq('id', visit.id)
            .eq('location_id', locationId)
            .eq('updated_at', visit.updated_at)
            .select('id')
            .single()
        : await supabase
            .from('location_visit_history')
            .insert(values)
            .select('id')
            .single();
      checkWrite(result);
    },
    onSuccess: onSaved,
  });
  const date = visit ? new Date(visit.visited_at) : new Date();
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
  return (
    <form
      aria-label={visit ? 'Edit visit' : 'Record visit'}
      className="space-y-4 rounded-lg border border-primary p-4"
      onSubmit={(event) => submitForm(event, save.mutate)}
    >
      <h3 className="font-medium text-text">
        {visit ? 'Edit visit' : 'Record visit'}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          name="visited_at"
          title="Visit date and time"
          type="datetime-local"
          value={localDate}
          required
        />
        <Input
          name="technician_name"
          title="Technician / crew"
          value={visit?.technician_name}
        />
      </div>
      <TextArea
        name="work_performed"
        title="Work completed"
        value={visit?.work_performed}
      />
      <Check
        name="is_customer_visible"
        title="Publish visit date, technician and work summary to the customer"
        checked={visit?.is_customer_visible}
      />
      <SaveState mutation={save} label="Save visit" onCancel={onCancel} />
    </form>
  );
}

function FollowUpEditor({
  visitId,
  record,
  onSaved,
}: {
  visitId: string;
  record?: PrivateVisit;
  onSaved: () => Promise<unknown>;
}) {
  const [identity, setIdentity] = useState(
    record ? { id: record.id, updated_at: record.updated_at } : null,
  );
  const save = useMutation({
    mutationFn: async (form: FormData) => {
      const values = { visit_id: visitId, follow_up: text(form, 'follow_up') };
      const result = identity
        ? await supabase
            .from('location_visit_private')
            .update(values)
            .eq('id', identity.id)
            .eq('visit_id', visitId)
            .eq('updated_at', identity.updated_at)
            .select('id,updated_at')
            .single()
        : await supabase
            .from('location_visit_private')
            .insert(values)
            .select('id,updated_at')
            .single();
      checkWrite(result);
      setIdentity(result.data!);
    },
    onSuccess: onSaved,
  });
  return (
    <form
      aria-label="Internal visit follow-up"
      className="space-y-3 rounded-lg border border-border p-4"
      onSubmit={(event) => submitForm(event, save.mutate)}
    >
      <TextArea
        name="follow_up"
        title="Internal follow-up"
        value={record?.follow_up}
      />
      <SaveState mutation={save} label="Save internal follow-up" />
    </form>
  );
}

function submitForm(
  event: FormEvent<HTMLFormElement>,
  mutate: (data: FormData) => void,
) {
  event.preventDefault();
  mutate(new FormData(event.currentTarget));
}
function checkWrite(result: {
  error: { code?: string; message: string } | null;
  data: unknown;
}) {
  if (result.error) {
    if (result.error.code === 'PGRST116' || result.error.code === '23505')
      throw new Error(
        'This record changed while you were editing. Reload the page and review the latest record before saving.',
      );
    throw result.error;
  }
  if (!result.data)
    throw new Error(
      'This record changed while you were editing. Reload the page before saving.',
    );
}
function Input({
  title,
  name,
  value,
  type = 'text',
  required,
  maxLength,
  min = 0,
  max,
}: {
  title: string;
  name: string;
  value?: string | number | null;
  type?: string;
  required?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
}) {
  return (
    <label className="block space-y-1 text-xs text-text-muted">
      <span>{title}</span>
      <input
        name={name}
        type={type}
        defaultValue={value ?? ''}
        required={required}
        min={type === 'number' ? min : undefined}
        max={max}
        step={type === 'number' ? 'any' : undefined}
        maxLength={maxLength}
        className={inputClass}
      />
    </label>
  );
}
function TextArea({
  title,
  name,
  value,
}: {
  title: string;
  name: string;
  value?: string | null;
}) {
  return (
    <label className="block space-y-1 text-xs text-text-muted">
      <span>{title}</span>
      <textarea
        name={name}
        defaultValue={value ?? ''}
        rows={3}
        className={inputClass}
      />
    </label>
  );
}
function Select({
  title,
  name,
  value,
  options,
}: {
  title: string;
  name: string;
  value: string;
  options: string[];
}) {
  return (
    <label className="block space-y-1 text-xs text-text-muted">
      <span>{title}</span>
      <select name={name} defaultValue={value} className={inputClass}>
        {options.map((option) => (
          <option key={option} value={option}>
            {label(option)}
          </option>
        ))}
      </select>
    </label>
  );
}
function Check({
  title,
  name,
  checked,
}: {
  title: string;
  name: string;
  checked?: boolean | null;
}) {
  return (
    <label className="flex min-h-11 items-center gap-2 text-sm text-text">
      <input
        type="checkbox"
        name={name}
        defaultChecked={checked ?? false}
        className="h-4 w-4 accent-primary"
      />
      {title}
    </label>
  );
}
function Fact({ title, value }: { title: string; value?: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-text-muted">{title}</dt>
      <dd className="mt-1 break-words text-text">{value || '—'}</dd>
    </div>
  );
}
function ErrorMessage({
  error,
  retry,
}: {
  error: unknown;
  retry?: () => void;
}) {
  return (
    <p role="alert" className="text-sm text-error">
      {error instanceof Error
        ? error.message
        : typeof error === 'object' && error && 'message' in error
          ? String(error.message)
          : 'The operation could not be completed.'}
      {retry && (
        <>
          {' '}
          <button type="button" className="underline" onClick={retry}>
            Try again
          </button>
        </>
      )}
    </p>
  );
}
function SaveState({
  mutation,
  label: title,
  onCancel,
}: {
  mutation: { isPending: boolean; isSuccess: boolean; error: unknown };
  label: string;
  onCancel?: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={mutation.isPending}
          className={primaryClass}
        >
          {mutation.isPending ? 'Saving…' : title}
        </button>
        {onCancel && (
          <button type="button" className={buttonClass} onClick={onCancel}>
            Cancel
          </button>
        )}
        {mutation.isSuccess && (
          <span role="status" className="text-sm text-success">
            Saved.
          </span>
        )}
      </div>
      {Boolean(mutation.error) && <ErrorMessage error={mutation.error} />}
    </div>
  );
}
